<script setup lang="ts">
  import { cva } from 'class-variance-authority'
  import type { TIcons } from '@/components/icon/types'
  import type { TButtonSize, TButtonType, TButtonVariant } from '@/components/ui/button/types'

  const props = withDefaults(
    defineProps<{
      title?: string
      variant?: TButtonVariant
      icon?: TIcons | null
      size?: TButtonSize
      disabled?: boolean
      type?: TButtonType
    }>(),
    {
      title: '',
      variant: 'primary',
      icon: null,
      size: 'md',
      disabled: false,
      type: 'button'
    }
  )

  const hasIcon = computed(() => !!props.icon && !props.title)
  const hasPrependIcon = computed(() => !!props.icon && !!props.title)

  const buttonBase = computed(() =>
    cva(
      'cursor-pointer focus-visible:ring-bt-blue-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:text-bt-blue-ui/35',
      {
        variants: {
          variant: {
            primary: 'hover:bg-bt-green-hover bg-bt-green text-bt-blue-dark',
            secondary: 'bg-bt-blue-ui text-white hover:bg-bt-blue-ui-light',
            outline: 'border-border border bg-transparent text-bt-blue-ui hover:bg-bt-blue-ui hover:text-white',
            link: 'bg-transparent text-bt-blue-ui hover:text-bt-blue-ui-light',
            none: ''
          },
          disabled: {
            false: '',
            primary: 'disabled:bg-bt-green/35',
            secondary: 'disabled:bg-bt-blue-ui/35 disabled:text-white/35',
            outline: 'hover:bg-transparent disabled:hover:bg-transparent',
            link: ''
          },
          hasPrependIcon: {
            true: 'flex items-center gap-2',
            false: ''
          },
          rounded: {
            default: 'rounded-sm',
            icon: 'rounded-full'
          },
          sm: {
            default: 'py-1 px-4',
            icon: 'p-1'
          },
          md: {
            default: 'py-2 px-4',
            icon: 'p-2'
          },
          lg: {
            default: 'py-3 px-6',
            icon: 'p-3'
          }
        }
      }
    )({
      variant: hasIcon.value && props.variant !== 'outline' ? 'none' : props.variant,
      [props.size]: hasIcon.value ? 'icon' : 'default',
      hasPrependIcon: hasPrependIcon.value,
      disabled: props.disabled ? props.variant : false,
      rounded: hasIcon.value ? (props.variant !== 'outline' ? 'default' : 'icon') : 'default'
    })
  )
</script>

<template>
  <button
    :class="buttonBase"
    :disabled="disabled"
    :type="type"
  >
    <Icon
      v-if="!!icon"
      :name="icon"
    />
    <template v-if="title">
      {{ title }}
    </template>
  </button>
</template>
